import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BiCheckbox } from "react-icons/bi";
import { FaBars, FaTimes } from "react-icons/fa";
import { Calendar, FileText, BookOpen } from "lucide-react";

function BecomeAMember() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  const API_BASE_URL = "https://backend.dlapakistan.com/api/member/";

  // State for posts
  const [posts, setPosts] = useState([]);
  const [contents, setContents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Window resize handle
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 1029) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobileView = windowWidth <= 1029;

  // Helper function to get image URL
  const getImageUrl = (post) => {
    if (!post) return "https://via.placeholder.com/300x150/059669/ffffff?text=No+Image";
    
    if (post.image_url) {
      return post.image_url;
    }
    
    if (post.image) {
      if (post.image.startsWith('http')) {
        return post.image;
      }
      if (post.image.startsWith('/media/')) {
        return `https://backend.dlapakistan.com${post.image}`;
      }
      return `https://backend.dlapakistan.com/media/posts/${post.image}`;
    }
    
    return "https://via.placeholder.com/300x150/059669/ffffff?text=No+Image";
  };

  // Load data from API
  useEffect(() => {
    loadData();
    loadContents();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const postsResponse = await fetch(`${API_BASE_URL}posts/`);
      
      if (!postsResponse.ok) {
        throw new Error('Failed to fetch posts from server');
      }

      const postsData = await postsResponse.json();
      setPosts(postsData);
      
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load contents from API
  const loadContents = async () => {
    try {
      const contentsResponse = await fetch(`${API_BASE_URL}contents/`);
      
      if (contentsResponse.ok) {
        const contentsData = await contentsResponse.json();
        setContents(contentsData);
      }
    } catch (err) {
      console.error("Error loading contents:", err);
    }
  };

  // Format date to readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? "Invalid date"
      : date.toLocaleDateString("en-US", { 
          year: "numeric", 
          month: "long", 
          day: "numeric" 
        });
  };

  // Generate sidebar links from posts buttons
  const getSidebarLinks = () => {
    const postsWithButtons = posts.filter(post => post.button && post.button.trim() !== "");
    const uniqueButtons = [...new Set(postsWithButtons.map(post => post.button.trim()))];
    
    const buttonLinks = uniqueButtons.map(buttonName => {
      const postWithButton = postsWithButtons.find(post => post.button === buttonName);
      const contentForButton = contents.find(content => content.button === buttonName);
      
      return {
        text: buttonName,
        border: "#ccc",
        showIcon: true,
        hasContent: !!contentForButton,
        post: postWithButton,
        content: contentForButton
      };
    });
    
    return buttonLinks;
  };

  // Handle sidebar click - Navigate to NewsPostDetail with proper slug
  const handleSidebarClick = (item) => {
    if (item.post && item.post.slug) {
      // Navigate to NewsPostDetail page with post's slug
      navigate(`/news/${item.post.slug}`);
    } else if (item.post && item.post.id) {
      // Fallback: Use post ID if slug not available
      navigate(`/news/${item.post.id}`);
    }
    
    if (isMobileView) {
      setIsSidebarOpen(false);
    }
  };

  // Handle post card click - Navigate to NewsPostDetail
  const handleCardClick = (post) => {
    if (post.slug) {
      navigate(`/news/${post.slug}`);
    } else if (post.id) {
      navigate(`/news/${post.id}`);
    } else {
      alert("This post doesn't have a valid link");
    }
  };

  // Handle button click on post card - Navigate to NewsPostDetail
  const handlePostButtonClick = (post, e) => {
    e.stopPropagation();
    
    if (post.slug) {
      navigate(`/news/${post.slug}`);
    } else if (post.id) {
      navigate(`/news/${post.id}`);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 pt-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#70B444]"></div>
        <p className="ml-4 text-gray-600">Loading posts...</p>
      </div>
    );
  }

  const sidebarLinks = getSidebarLinks();

  return (
    <div className="w-full min-h-screen bg-gray-50 pt-64">
      
      {/* Breadcrumb */}
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        <div className="flex flex-wrap items-center text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/")}
          >
            Home
          </span> 
          <span className="mx-2">»</span>
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/about")}
          >
            About The DLA
          </span>
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium">
            Become a Member
          </span>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto pb-12">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative">
          {/* DESKTOP SIDEBAR - ONLY IF THERE ARE BUTTON LINKS */}
          {!isMobileView && sidebarLinks.length > 0 && (
            <div className="w-full lg:w-72 xl:w-80 shrink-0 bg-[#f7f9fc] p-4 md:p-5 rounded-lg shadow-sm h-fit">
              <div className="bg-[#70B444] text-white p-3 text-center mb-4">
                <h3 className="m-0 text-base lg:text-lg font-semibold">Become a Member</h3>
              </div>
              {sidebarLinks.map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleSidebarClick(item)}
                  className={`flex items-center justify-between py-2.5 text-[#70B444] font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-2 text-sm lg:text-base ${
                    item.border ? "border-b border-gray-200" : ""
                  }`}
                >
                  <div className="flex items-center">
                    {item.showIcon && <BiCheckbox className="mr-2 text-lg flex-shrink-0" />}
                    <span className="break-words">{item.text}</span>
                  </div>
                  {item.hasContent && (
                    <BookOpen className="text-[#70B444] text-sm ml-2" size={16} title="Has additional content" />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* MAIN CONTENT AREA */}
          <div className={`flex-1 ${isMobileView ? 'p-0' : 'p-2 md:p-4'} ${
            isMobileView && isSidebarOpen ? "opacity-30 pointer-events-none" : "opacity-100"
          }`}>
            {/* Page Title */}
            <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-3 md:pb-4 mb-4 md:mb-6">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#70B444] leading-snug">
                Become a Member
              </h1>
              
              {isMobileView && (
                <button
                  onClick={toggleSidebar}
                  className="flex items-center gap-2 text-[#70B444] border border-[#70B444] rounded-lg px-3 py-2 hover:bg-[#70B444] hover:text-white transition-all text-sm font-semibold shrink-0"
                >
                  {isSidebarOpen ? <FaTimes /> : <FaBars />}
                  <span>{isSidebarOpen ? "Close" : "Menu"}</span>
                </button>
              )}
            </div>

            {/* Posts Section */}
            <div className="bg-white rounded-xl shadow-sm p-6 mt-8">
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center gap-3">
                  <FileText className="text-[#70B444]" size={24} />
                  <h3 className="text-2xl font-bold text-gray-900">News Posts</h3>
                  <span className="bg-[#e8f5e9] text-[#70B444] px-3 py-1 rounded-full text-sm font-medium">
                    {posts.length} {posts.length === 1 ? "Post" : "Posts"}
                  </span>
                </div>
                {sidebarLinks.length > 0 && (
                  <div className="text-gray-500 text-sm">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                      Click buttons to view details
                    </span>
                  </div>
                )}
              </div>

              {posts.length === 0 ? (
                <div className="text-center py-16 border-2 border-dashed border-gray-300 rounded-xl bg-transparent">
                  <FileText className="mx-auto text-gray-300 mb-4" size={56} />
                  <p className="text-gray-400 text-lg">No posts available.</p>
                  <p className="text-gray-400 text-sm mt-2">
                    Add posts with buttons in admin panel to see them here
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {posts.map((post) => {
                    const contentForPost = contents.find(content => content.button === post.button);
                    return (
                      <div
                        key={post.id}
                        onClick={() => handleCardClick(post)}
                        className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all duration-300 bg-white group flex flex-col h-[320px] cursor-pointer"
                      >
                        {/* Image Section */}
                        <div className="h-32 relative bg-gray-100 overflow-hidden flex-shrink-0">
                          <img
                            src={getImageUrl(post)}
                            alt={post.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/300x150/70B444/ffffff?text=Error";
                            }}
                          />
                        </div>

                        {/* Content Section */}
                        <div className="p-3 flex flex-col flex-grow">
                          <h4 className="font-semibold text-base text-gray-900 mb-1 line-clamp-2">{post.title}</h4>

                          <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
                            <Calendar size={10} />
                            <span className="text-[11px]">{formatDate(post.created_at)}</span>
                          </div>

                          <div className="mb-2 flex-grow">
                            <div className="text-gray-600 text-xs line-clamp-2">
                              {post.description.replace(/<[^>]*>/g, "").substring(0, 80)}
                              {post.description.length > 80 ? "..." : ""}
                            </div>
                          </div>

                          <div className="flex justify-between items-center mt-auto">
                            <span className="px-2 py-1 border border-[#70B444] text-[#70B444] rounded text-xs font-medium">
                              Click to view post
                            </span>

                            {post.button && (
                              <button 
                                onClick={(e) => handlePostButtonClick(post, e)}
                                className={`px-2 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1 ${
                                  contentForPost 
                                    ? "bg-[#70B444] text-white hover:bg-[#5d9a3a]" 
                                    : "bg-gray-300 text-gray-600"
                                }`}
                                title={
                                  contentForPost 
                                    ? `View additional content for "${post.button}"` 
                                    : `View post details`
                                }
                              >
                                {post.button}
                                {contentForPost && <BookOpen size={10} />}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* MOBILE SIDEBAR - ONLY IF THERE ARE BUTTON LINKS */}
          {isMobileView && sidebarLinks.length > 0 && (
            <>
              <div
                className={`fixed top-0 right-0 h-full w-[85vw] max-w-sm bg-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out ${
                  isSidebarOpen ? "translate-x-0" : "translate-x-full"
                }`}
              >
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b flex justify-between items-center bg-[#70B444] text-white">
                    <span className="font-bold text-lg">Become A Member</span>
                    <FaTimes 
                      className="cursor-pointer text-2xl" 
                      onClick={toggleSidebar} 
                    />
                  </div>
                  <div className="overflow-y-auto flex-1 p-2">
                    {sidebarLinks.map((item, index) => (
                      <div
                        key={index}
                        onClick={() => handleSidebarClick(item)}
                        className={`p-3 md:p-4 text-base border-b border-gray-100 flex items-center justify-between hover:bg-gray-50 ${
                          location.pathname === `/news/${item.post?.slug}` 
                            ? "text-[#70B444] font-semibold bg-green-50 border-l-4 border-l-[#70B444]" 
                            : "text-gray-700"
                        }`}
                      >
                        <div className="flex items-center">
                          {item.showIcon && <BiCheckbox className="mr-3 text-xl text-[#70B444] flex-shrink-0" />}
                          <span>{item.text}</span>
                        </div>
                        {item.hasContent && (
                          <BookOpen className="text-[#70B444] text-sm" size={16} title="Has additional content" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Overlay */}
              {isMobileView && isSidebarOpen && (
                <div
                  className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
                  onClick={toggleSidebar}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default BecomeAMember;